import React from 'react';
import { Alert, Box, Chip, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import RemoteAutocomplete from '../components/RemoteAutocomplete';

// Whose replies the nightly AI training learns from (CP-8406). Backend config keys:
// ai_learning_scope ('all' | 'selected', default 'all') + ai_learning_user_ids. Narrowing the
// selection rebuilds the auto-generated training files on the next nightly run.

export const normalizeTeamMember = (u) => ({
  id: u?.id,
  display_name: u?.display_name || u?.name || '',
  email: u?.email || '',
});

export const teamMemberLabel = (u) => {
  if (u == null) return '';
  if (typeof u !== 'object') return `User #${u}`;
  return u.display_name || u.name || u.email || `User #${u.id ?? ''}`;
};

const radioLabel = (text, selected) => (
  <Typography variant="body2" sx={{ fontWeight: selected ? 600 : 400, color: selected ? 'text.primary' : 'text.disabled' }}>
    {text}
  </Typography>
);

export default function AILearningSourceSetting({ scope, users, disabled, onScopeChange, onUsersChange }) {
  return (
    <Box sx={{ mb: 1, pl: { xs: 1, sm: 9.75 }, maxWidth: 520 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
        Who the AI learns from
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
        Every night the AI studies how your team edited or replaced its suggested replies and adds what
        it learns to its auto-generated training files. Choose whose replies it trains on.
      </Typography>
      <RadioGroup
        row
        value={scope}
        onChange={(e) => onScopeChange(e.target.value)}
        sx={{ gap: { xs: 0.5, sm: 3 }, flexDirection: { xs: 'column', sm: 'row' } }}
      >
        <FormControlLabel
          value="all"
          disabled={disabled}
          control={<Radio size="small" />}
          label={radioLabel('Everyone on the team', scope === 'all')}
        />
        <FormControlLabel
          value="selected"
          disabled={disabled}
          control={<Radio size="small" />}
          label={radioLabel('Only selected team members', scope === 'selected')}
        />
      </RadioGroup>
      {scope === 'selected' && (
        <Box sx={{ mt: 1.5 }}>
          <RemoteAutocomplete
            multiple
            filterSelected
            resource="user"
            apiFilters={[{ field: 'regular_users', op: 'eq', value: false }]}
            value={users}
            onChange={(e, v) => onUsersChange((v || []).map(normalizeTeamMember).filter((u) => u.id != null))}
            disabled={disabled}
            setOptionsCount={() => {}}
            filterOptions={(options) => options}
            fullWidth
            size="small"
            label="Team members to learn from"
            variant="outlined"
            clearOnBlur={false}
            getOptionLabel={teamMemberLabel}
            isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
            renderOption={(liProps, option) => {
              const name = teamMemberLabel(option);
              return (
                <li {...liProps} key={option.id}>
                  <div>
                    <div>{name}</div>
                    {option.email && option.email !== name && (
                      <Typography variant="caption" color="text.secondary">{option.email}</Typography>
                    )}
                  </div>
                </li>
              );
            }}
            renderTags={(value, getTagProps) =>
              value.map((opt, i) => (
                <Chip {...getTagProps({ index: i })} size="small" label={teamMemberLabel(opt)} />
              ))
            }
          />
          {users.length === 0 ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Nobody selected — the AI won't learn from anyone's replies until you add at least one team member.
            </Alert>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Removing someone (or switching from everyone) rebuilds the auto-generated training files from
              the selected team members' replies on the next nightly update.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
